/**
 * Submit data to a form to HubSpot
 *
 * More info here:  https://developers.hubspot.com/docs/methods/forms/submit_form_v3
 */

import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { getCookie } from '../../utilities/cookies'
import { _hsq } from '../util/_hsq'
import { createIdentifyData } from '../util/createIdentifyData'

export const detectPageName = Symbol('detectPageName')
export const detectPageUri = Symbol('detectPageUri')

export type HubspotSubmitOptions = {
    pageName?: Symbol | string
    pageUri?: Symbol | string
    portalId?: string
    skipValidation?: boolean
    skipIdentify?: boolean
    consentToProcess?: boolean
    consentToEmail?: boolean
}

const defaultSubmitOptions: HubspotSubmitOptions = {
    pageName: detectPageName,
    pageUri: detectPageUri,
    portalId: '3520736',
    skipValidation: true,
    skipIdentify: false,
    consentToProcess: true,
    consentToEmail: false,
}

@Injectable({
    providedIn: 'root',
})
export class HubspotFormService {
    constructor(private http: HttpClient) {}

    /**
     * Submit form to hubSpot
     * @param portalId
     * @param formGuid
     * @param pageUri
     * @param pageName
     * @param fields
     * @param skipValidation
     */
    public async submitForm(
        formGuid: string,
        fields: { name: string; value: string }[],
        options?: HubspotSubmitOptions
    ) {
        options = { ...defaultSubmitOptions, ...(options || {}) }

        if (options.pageName === detectPageName) {
            options.pageName = document.title
        }

        if (options.pageUri === detectPageUri) {
            options.pageUri = window.location.href
        }

        const context: {
            pageUri: string
            pageName: string
            hutk?: string
        } = {
            pageUri: options.pageUri.toString(),
            pageName: options.pageName.toString(),
        }

        let legalConsentOptions: any | false = false

        if (options.consentToProcess === true) {
            legalConsentOptions = {
                consent: {
                    consentToProcess: true,
                    text:
                        $localize`@@privacy-policy.before-link` +
                        $localize`@@privacy-policy.link-text` +
                        $localize`@@privacy-policy.after-link`,
                },
            }

            if (options.consentToEmail === true) {
                legalConsentOptions.consent.communications = [
                    {
                        value: true,
                        subscriptionTypeId: '5323913',
                        text: 'I agree to receive other communications from MisterGreen.',
                    },
                    {
                        value: true,
                        subscriptionTypeId: '6308077',
                        text: 'I agree to receive other communications from MisterGreen.',
                    },
                    {
                        value: true,
                        subscriptionTypeId: '5323909',
                        text: 'I agree to receive other communications from MisterGreen.',
                    },
                    {
                        value: true,
                        subscriptionTypeId: '4144791',
                        text: 'I agree to receive other communications from MisterGreen.',
                    },
                    {
                        value: true,
                        subscriptionTypeId: '5402732',
                        text: 'I agree to receive other communications from MisterGreen.',
                    },
                ]
            }
        }

        const hutk = getCookie('hubspotutk')
        if (hutk) {
            context.hutk = hutk
        }

        const result = await this.http
            .post(
                `https://api.hsforms.com/submissions/v3/integration/submit/${options.portalId}/${formGuid}`,
                {
                    fields,
                    ...(options.skipValidation ? { skipValidation: options.skipValidation } : {}),
                    context,
                    ...(legalConsentOptions ? { legalConsentOptions } : {}),
                },
                {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                    }),
                }
            )
            .toPromise()

        const emailField = fields.find(({ name }) => name === 'email')

        if (!options.skipIdentify && emailField) {
            setTimeout(() => {
                _hsq.push(['identify', createIdentifyData({ email: emailField.value })])
            }, 500)
        }

        return result
    }
}
